import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

interface FiguraProfessionale {
  id: number;
  nome: string;
  costo_giornaliero: number;
  costo_orario: number;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

const FigureProfessionaliPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [figure, setFigure] = useState<FiguraProfessionale[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFigura, setEditingFigura] = useState<FiguraProfessionale | null>(null);
  const [formData, setFormData] = useState<Partial<FiguraProfessionale>>({
    enabled: true,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFigure();
  }, []);

  const loadFigure = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/figure-professionali`, { headers });
      if (!response.ok) {
        console.error('Failed to load figure professionali');
        setFigure([]);
        setError('Errore nel caricamento delle figure professionali');
        return;
      }
      const data = await response.json();
      setFigure(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading figure professionali:', err);
      setFigure([]);
      setError('Errore nel caricamento delle figure professionali');
    }
  };

  const handleOpenDialog = (figura?: FiguraProfessionale) => {
    if (figura) {
      setEditingFigura(figura);
      setFormData(figura);
    } else {
      setEditingFigura(null);
      setFormData({ enabled: true });
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFigura(null);
    setFormData({ enabled: true });
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.nome || !formData.nome.trim()) {
        setError('Nome figura professionale obbligatorio');
        return;
      }

      if (formData.costo_giornaliero === undefined || formData.costo_giornaliero === null) {
        setError('Costo giornaliero obbligatorio');
        return;
      }

      if (formData.costo_orario === undefined || formData.costo_orario === null) {
        setError('Costo orario obbligatorio');
        return;
      }

      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const url = editingFigura
        ? `${BASE_URL}/figure-professionali/${editingFigura.id}`
        : `${BASE_URL}/figure-professionali`;

      const method = editingFigura ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nel salvataggio');
      }

      loadFigure();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (id: number, nome: string) => {
    if (!window.confirm(`Eliminare la figura professionale "${nome}"?`)) return;

    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/figure-professionali/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nell\'eliminazione');
      }

      loadFigure();
    } catch (err: any) {
      alert(err.message || 'Errore nell\'eliminazione');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Figure Professionali
        </Typography>
        {hasPermission('risorse', 'create') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nuova Figura Professionale
          </Button>
        )}
      </Box>

      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Costo Giornaliero</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Costo Orario</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Stato</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {figure.map((figura) => (
              <TableRow key={figura.id} hover>
                <TableCell>{figura.nome}</TableCell>
                <TableCell align="right">{figura.costo_giornaliero.toFixed(2)} €</TableCell>
                <TableCell align="right">{figura.costo_orario.toFixed(2)} €</TableCell>
                <TableCell align="center">
                  {figura.enabled ? (
                    <Chip label="Attivo" color="success" size="small" />
                  ) : (
                    <Chip label="Disattivo" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {hasPermission('risorse', 'update') && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(figura)}
                    >
                      <Edit />
                    </IconButton>
                  )}
                  {hasPermission('risorse', 'delete') && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(figura.id, figura.nome)}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog per creazione/modifica */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingFigura ? 'Modifica Figura Professionale' : 'Nuova Figura Professionale'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Nome Figura Professionale"
            type="text"
            fullWidth
            required
            value={formData.nome || ''}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Costo Giornaliero (€)"
            type="number"
            fullWidth
            required
            value={formData.costo_giornaliero || ''}
            onChange={(e) => setFormData({ ...formData, costo_giornaliero: parseFloat(e.target.value) })}
            sx={{ mt: 2 }}
            inputProps={{ step: "0.01", min: "0" }}
          />
          <TextField
            margin="dense"
            label="Costo Orario (€)"
            type="number"
            fullWidth
            required
            value={formData.costo_orario || ''}
            onChange={(e) => setFormData({ ...formData, costo_orario: parseFloat(e.target.value) })}
            sx={{ mt: 2 }}
            inputProps={{ step: "0.01", min: "0" }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled !== false}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            }
            label="Attivo"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FigureProfessionaliPage;
